import React from 'react';
import './LegalPage.css';

interface TermsOfServiceProps {
  onNavigate: (view: 'home' | 'privacy') => void;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onNavigate }) => {
  return (
    <div className="legal-container">
      <div className="legal-header">
        <div className="legal-nav-bar">
          <button className="legal-back-btn" onClick={() => onNavigate('home')}>
            ← Back to Game
          </button>
          <div className="legal-quick-switch">
            <span
              className="legal-switch-link"
              onClick={() => onNavigate('privacy')}
              role="button"
              tabIndex={0}
            >
              Privacy Policy →
            </span>
          </div>
        </div>
        <h1 className="legal-title">Terms of Service</h1>
        <p className="legal-last-updated">Last Updated: 20 September 2026</p>
      </div>

      <div className="legal-content">
        <article className="terms-of-service">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing, registering for, or playing Euro Hunting Simulator (&ldquo;the Game&rdquo;, &ldquo;the Service&rdquo;), 
              you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you do not agree to these Terms, 
              do not access or use the Service.
            </p>
          </section>

          <section>
            <h2>2. Eligibility</h2>
            <p>
              The Service is intended solely for individuals who are at least 13 years of age. If you are between 
              the ages of 13 and the age of legal majority in your jurisdiction, you represent and warrant that you 
              have obtained permission from a parent or legal guardian to access and use the Service.
            </p>
          </section>

          <section>
            <h2>3. Accounts and Authentication</h2>
            <p>
              Access to the Game requires authentication via a third-party service (such as Discord). You are 
              responsible for maintaining the security of your authentication credentials and account access. 
              We reserve the right to terminate, suspend, or restrict any account at our sole discretion if 
              these Terms are violated.
            </p>
          </section>

          <section>
            <h2>4. Virtual Items and Game Economy</h2>
            <p>
              All virtual items, rolls, currencies, inventory, and progression metrics within the Game represent 
              a limited, personal, non-exclusive, non-transferable, and revocable license to use digital gameplay features. 
              Virtual items and currencies:
            </p>
            <ul>
              <li>Have no real-world monetary value.</li>
              <li>Cannot be redeemed, refunded, or exchanged for fiat currency or real-world goods.</li>
              <li>Are subject to rebalancing, modification, or removal at our discretion for gameplay or technical reasons.</li>
            </ul>
          </section>

          <section>
            <h2>5. Purchases and Refunds</h2>
            <p>
              All purchases of virtual items, currencies, or access are final and non-refundable, except where 
              required by applicable law or where an exception is granted at our sole discretion upon contacting 
              us at <a href="mailto:contact@euro-hunting-simulator.com">contact@euro-hunting-simulator.com</a>.
            </p>
            <p>
              If any payment is refunded, reversed, or charged back for any reason, we reserve the right to 
              adjust your in-game account balance (including applying a negative balance), revoke any items or 
              progression obtained directly or indirectly from that transaction, or terminate account access.
            </p>
          </section>

          <section>
            <h2>6. Acceptable Use and Fair Play</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Exploit bugs, reverse-engineer, decompile, or scrape the Game's code or assets without permission.</li>
              <li>Use automated tools, bots, scripts, or macros to manipulate gameplay, rolls, or progression.</li>
              <li>Interfere with or disrupt the operation of the Game, servers, or underlying networks.</li>
            </ul>
          </section>

          <section>
            <h2>7. Disclaimer of Warranties</h2>
            <p>
              The Game and all associated services are provided on an &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo; basis without 
              warranties of any kind, whether express or implied. We do not guarantee uninterrupted availability, 
              bug-free operation, or that saved data or progression will never be lost.
            </p>
          </section>

          <section>
            <h2>8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, in no event shall we be liable for any indirect, 
              incidental, special, consequential, or punitive damages arising out of or related to your access 
              to or use of the Service.
            </p>
            <p>
              Our total aggregate liability for any direct claim arising under or relating to these Terms or the 
              Service shall not exceed the greater of: (a) the total amount paid by you to us in the twelve (12) 
              months preceding the claim, or (b) &euro;50. Nothing in these Terms excludes or limits liability that 
              cannot be excluded or limited under mandatory consumer protection laws.
            </p>
          </section>

          <section>
            <h2>9. Changes to Terms</h2>
            <p>
              We reserve the right to update or modify these Terms at any time. Changes take effect upon posting, 
              reflected by the &ldquo;Last Updated&rdquo; date at the top of this document. Material changes will be communicated 
              via a prominent notice on the website or within the Game. Your continued use of the Game following 
              any modification constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2>10. Contact Information</h2>
            <p>
              For questions, support, or inquiries regarding these Terms of Service, please contact us at:{' '}
              <a href="mailto:contact@euro-hunting-simulator.com">contact@euro-hunting-simulator.com</a>.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};
